import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataStoreService } from "../shared/services/api/data-store.service";
import { Site } from "../shared/models/site";
import { ActivatedRoute, NavigationExtras, Router } from "@angular/router";
import { GeolocationService } from "../shared/services/geo-location.service";
import { Subscription } from "rxjs";
import { NavigationService } from "../shared/services/navigation-service";
import { LoadingStateService } from "../shared/services/loading-state.service";
import { CustomerCategory } from "../shared/models/customer-category";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subject } from "rxjs/Subject";
import { JsonApiQueryData } from "angular2-jsonapi";
import { Observable } from "rxjs/Observable";

interface Coordinates {
  lat: number,
  lng: number
}


@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css']
})
export class MapComponent implements OnInit, OnDestroy {

  private readonly minDistanceBetweenLoads = 3000;


  public centerCoordinates: Coordinates = { lat: 0, lng: 0 };
  public sites: Site[] = [];
  private locationSubscription: Subscription;
  private siteLoadingSubscription: Subscription;
  public categories: CustomerCategory[] = [];

  public filterForm: FormGroup = null;
  private categoryControls: FormGroup;

  private locationSubject = new Subject<Coordinates>();

  public openSite = null;
  public showFilter = true;

  constructor(private dataStore: DataStoreService,
              private router: Router,
              private formBuilder: FormBuilder,
              private geoLocationService: GeolocationService,
              private activatedRoute: ActivatedRoute,
              private loadingState: LoadingStateService) {

  }

  ngOnInit() {
    this.loadingState.setLoading(true);
    this.dataStore.findAll(CustomerCategory).subscribe(doc => {
      this.categories = doc.getModels();
      this.createForm();
      this.bindSiteLoading();

      this.activatedRoute.paramMap.subscribe(params => {
        if (params.has("search")) {
          this.filterForm.controls["search"].patchValue(params.get("search"));
        } else {
          this.filterForm.controls["search"].patchValue("");
        }
      });

      this.loadingState.setLoading(false);
    });

  }

  createForm() {
    let controls = {};
    this.categories.forEach(category => {
      controls[category.id] = this.formBuilder.control(false);
    });
    this.categoryControls = this.formBuilder.group(controls);

    this.filterForm = this.formBuilder.group({
      "today": [false],
      "categories": this.categoryControls,
      "search": [""]
    });


  }

  bindSiteLoading() {
    let locationChanges = this.geoLocationService.location
      .map(position => {
        return { lat: position.coords.latitude, lng: position.coords.longitude }
      });

    this.locationSubscription = locationChanges.subscribe(position => {
      this.centerCoordinates = position;
    });

    this.siteLoadingSubscription = this.locationSubject.asObservable().merge(locationChanges)
      .distinctUntilChanged((a, b) => {
        return GeolocationService.distance(a, b) < this.minDistanceBetweenLoads
      })
      .debounceTime(100)
      .combineLatest(this.filterForm.valueChanges.distinctUntilChanged().debounceTime(300).startWith({}))
      .switchMap(request => {
        return this.searchSites(this.filterForm != null ? this.filterForm.value : null, request[0])
      }).subscribe(sites => this.updateSites(sites));
  }

  updateSites(document: JsonApiQueryData<Site>) {
    this.openSite = null;
    this.sites = document.getModels();
    if (this.sites.length == 1) {
      this.centerCoordinates.lat = this.sites[0].address.lat;
      this.centerCoordinates.lng = this.sites[0].address.lng;
    }
  }

  searchSites(formValue: any, location: Coordinates) {
    this.loadingState.setLoading(true);

    let request: Observable<JsonApiQueryData<Site>>;

    if (this.filtersAreSet(formValue)) {
      let enabledCategories = formValue == null ? [] : Object.keys(formValue["categories"])
        .filter(id => formValue["categories"][id])
        .map(value => parseInt(value));

      this.routeToParameterized({
        search: formValue.search,
      });
      request = this.dataStore.findAll(Site, {
        filter: {
          query: formValue["search"],
          today: formValue["today"],
          categories: enabledCategories,
        }
      });
    } else {
      this.routeToParameterized({});
      request = this.dataStore.findAll(Site, {
        filter: location
      })
    }
    return request.finally(() => this.loadingState.setLoading(false));
  }

  private routeToParameterized(form: any) {
    const extras: NavigationExtras = {
      fragment: this.activatedRoute.snapshot.fragment,
      queryParams: this.activatedRoute.snapshot.queryParams,
    };
    var params = Object.assign({}, this.activatedRoute.snapshot.params);
    if (form.search) {
      params["search"] = form.search
    } else {
      delete params["search"];
    }

    this.router.navigate([params], extras);
  }

  navigateTo(site: Site) {
    NavigationService.navigateTo(site);
  }

  ngOnDestroy(): void {
    this.locationSubscription.unsubscribe();
    this.siteLoadingSubscription.unsubscribe();
  }

  centerChange(event: any) {
    if (this.filterForm != null && !this.filtersAreSet(this.filterForm.value) && this.openSite == null) {
      this.locationSubject.next(event);
    }
  }


  private filtersAreSet(formValue: any) {
    let enabledCategories = formValue == null ? [] : Object.keys(formValue["categories"])
      .filter(id => formValue["categories"][id])
      .map(value => parseInt(value));
    return (formValue != null && (formValue.search.length > 0 || enabledCategories.length > 0 || formValue.today === true));
  }

}



// WEBPACK FOOTER //
// /home/markush/Projects/fieldsale-wingman-client/src/app/map/map.component.ts